import classNames from 'classnames';
import { IReactionDisposer, observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import { PureComponent } from 'react';
import {
  Badge,
  Button,
  ToggleButton,
  ButtonGroup,
  InputGroup,
  Form,
  Row,
  Tab,
  Tabs,
  Spinner
} from 'react-bootstrap';
import BigNumber from 'bignumber.js';

import { toastStore } from '../../../component/Toast';
import { RangeInput } from '../../../component/RangeInput';
import { ChooseWallet } from '../../../component/ChooseWallet';
import * as style from './index.module.less';
import session from '../../../model/Session';
import {
  formatNumber,
  getPrecision,
  parseToCommon,
  parseToOrigin,
  toBigInt,
  toBigNumber,
  toPrecision,
  zero
} from '../../../model/utils/helper';
import { DFT, TokenInfo } from '../../../model/canister/DFT';
import { DepthItem } from '../../../model/canister/Depth';
import { SpotOrderType } from '../../../model/canister/Fusion';
import { DEFAULT_APPROVE_AMOUNT } from '../../../model/utils/config';
import { CanisterError } from '../../../model/utils/exception';

export interface TradeFormProps {
  className?: string;
  marketId: string;
  priceDecimals?: number;
  bidOrderDepthClickItem?: DepthItem;
  askOrderDepthClickItem?: DepthItem;
  orderVolumePrecision?: number;
  orderPricePrecision?: number;
  maxAskPriceLimit?: number;
  maxBidPriceLimit?: number;
  baseToken?: TokenInfo;
  quoteToken?: TokenInfo;
  baseTokenPrecision?: number;
  quoteTokenPrecision?: number;
  latestPrice?: number;
  onAskOrderSubmitted?: (price: bigint, volume: bigint) => Promise<bigint>;
  onBidOrderSubmitted?: (price: bigint, volume: bigint) => Promise<bigint>;
}

interface ActionState {
  approvedAmount: BigNumber;
  submitting: boolean;
  input: ActionInput;
  validation: ActionValidation;
}

const defaultActionState: ActionState = {
  approvedAmount: zero,
  submitting: false,
  input: {
    price: zero,
    pricePrecision: 0,
    amount: zero,
    amountPrecision: 0,
    percent: 0,
    total: new BigNumber(0)
  },
  validation: {
    priceValid: true,
    priceInvalidMsg: '',
    amountValid: true,
    amountInvalidMsg: ''
  }
};
interface ActionInput {
  price: BigNumber;
  pricePrecision: number;
  amount: BigNumber;
  amountPrecision: number;
  percent: number;
  total: BigNumber;
}

interface ActionValidation {
  priceValid: boolean;
  priceInvalidMsg: string;
  amountValid: boolean;
  amountInvalidMsg: string;
}

@observer
export class TradeForm extends PureComponent<TradeFormProps> {
  @observable
  baseTokenBalance = new BigNumber(0);

  @observable
  quoteTokenBalance = new BigNumber(0);

  @observable
  bidState = defaultActionState;

  @observable
  askState = defaultActionState;

  private updateBalanceTimer?: NodeJS.Timer;
  private baseTokenActor: DFT;
  private quoteTokenActor: DFT;
  private updateBalanceInterval = 10 * 1000;
  private activeIDisposers: IReactionDisposer[] = [];

  /**
   * trade form input price changed
   */
  onTradeFormPriceChanged = (price: BigNumber, type: SpotOrderType) => {
    if (this.checkPricePrecision(price))
      switch (type) {
        case SpotOrderType.Bid:
          return this.onBidPriceChanged(price);
        case SpotOrderType.Ask:
          return this.onAskPriceChanged(price);
      }
  };

  /**
   * trade form input amount changed
   */
  onTradeFormAmountChanged = (amount: BigNumber, type: SpotOrderType) => {
    if (this.checkAmountPrecision(amount))
      switch (type) {
        case SpotOrderType.Bid:
          return this.onBidAmountChanged(amount);
        case SpotOrderType.Ask:
          return this.onAskAmountChanged(amount);
      }
  };

  /**
   * trade form input percent changed
   *
   * @param percent the percent value of the slider
   */
  onTradeFormSliderPercentChanged = (percent: number, type: SpotOrderType) => {
    switch (type) {
      case SpotOrderType.Bid:
        return this.onBidSliderPercentChanged(percent);
      case SpotOrderType.Ask:
        return this.onAskSliderPercentChanged(percent);
    }
  };

  /**
   * submit trade action
   */
  onTradeFormSubmit = (type: SpotOrderType) => {
    switch (type) {
      case SpotOrderType.Bid:
        return this.onBidOrderSubmitted();
      case SpotOrderType.Ask:
        return this.onAskOrderSubmitted();
    }
  };

  /**
   * Regularly update user's balance
   */
  updateTokenBalances = async () => {
    const userAddress = session?.walletAuth?.principal,
      { baseToken, quoteToken } = this.props;

    const baseTokenDecimals = baseToken?.decimals || 0;
    const quoteTokenDecimals = quoteToken?.decimals || 0;

    if (!userAddress) return;

    const baseRes = await this.baseTokenActor.balanceOf(userAddress);

    this.baseTokenBalance = parseToCommon(baseRes.balance, baseTokenDecimals);

    const quoteRes = await this.quoteTokenActor.balanceOf(userAddress);

    this.quoteTokenBalance = parseToCommon(
      quoteRes.balance,
      quoteTokenDecimals
    );
    console.info(
      `baseTokenBalance: ${this.baseTokenBalance}, quoteTokenBalance: ${this.quoteTokenBalance}`
    );
  };

  /**
   * Regularly update user's allowances
   */
  updateTokenAllowances = async () => {
    const userAddress = session.walletAuth?.principal;
    const { marketId, baseToken, quoteToken } = this.props;

    if (!userAddress || !marketId) return;

    const baseAllowance = await this.baseTokenActor.getTokenAllowance(
      userAddress,
      marketId
    );
    this.askState.approvedAmount = toBigNumber(baseAllowance).shiftedBy(
      -baseToken.decimals
    );
    const quoteAllowance = await this.quoteTokenActor.getTokenAllowance(
      userAddress,
      marketId
    );
    this.bidState.approvedAmount = toBigNumber(quoteAllowance).shiftedBy(
      -quoteToken.decimals
    );

    console.info(
      `base token allowance: ${this.bidState.approvedAmount}, quote token balance: ${this.askState.approvedAmount}`
    );
  };

  /**
   * call updateTokenBalances & updateTokenAllowances
   */
  startTimer = () => {
    this.updateBalanceTimer = setInterval(() => {
      this.updateTokenBalances();
      this.updateTokenAllowances();
    }, this.updateBalanceInterval);
  };

  /**
   * should clear the timer when component unmount
   */
  stopTimer = () => {
    if (this.updateBalanceTimer) {
      clearInterval(this.updateBalanceTimer);
      this.updateBalanceTimer = undefined;
    }
  };

  /**
   * stop & start
   */
  restartTimer = () => {
    this.stopTimer();
    this.startTimer();
  };

  /**
   * 1. restart balances fetching timer when token info changed
   * 2. update trade form price when depth order clicked
   */
  async componentDidMount() {
    const updateBalancesReactionDisposer = reaction(
      () => (this.props.baseToken, this.props.quoteToken),
      async (baseToken, quoteToken) => {
        if (!baseToken || !quoteToken) return;
        this.baseTokenActor = new DFT(this.props.baseToken.id);
        this.quoteTokenActor = new DFT(this.props.quoteToken.id);
        await this.updateTokenBalances();
        await this.updateTokenAllowances();
        this.restartTimer();
      },
      { fireImmediately: true }
    );
    this.activeIDisposers.push(updateBalancesReactionDisposer);

    const bidOrderDepthClickItemDisposer = reaction(
      () => this.props.bidOrderDepthClickItem,
      item => {
        if (!item) return;
        const price = Number(item.price);
        const priceBN = new BigNumber(price);
        this.bidState.input.price = priceBN;
        this.askState.input.price = priceBN;
        this.bidState.input.pricePrecision = this.props.orderPricePrecision;
        this.askState.input.pricePrecision = this.props.orderPricePrecision;
        this.onTradeFormPriceChanged(priceBN, SpotOrderType.Bid);
        this.onTradeFormPriceChanged(priceBN, SpotOrderType.Ask);
        console.info('this.bidState.input.price', Number(item.price));
      }
    );
    this.activeIDisposers.push(bidOrderDepthClickItemDisposer);

    const askOrderDepthClickItemDisposer = reaction(
      () => this.props.askOrderDepthClickItem,
      item => {
        if (!item) return;
        const price = Number(item.price);
        const priceBN = new BigNumber(price);
        this.bidState.input.price = priceBN;
        this.askState.input.price = priceBN;
        this.bidState.input.pricePrecision = this.props.orderPricePrecision;
        this.askState.input.pricePrecision = this.props.orderPricePrecision;
        this.onTradeFormPriceChanged(priceBN, SpotOrderType.Bid);
        this.onTradeFormPriceChanged(priceBN, SpotOrderType.Ask);
        console.info('this.sellState.input.price', Number(item.price));
      }
    );
    this.activeIDisposers.push(askOrderDepthClickItemDisposer);
  }

  /**
   * clear all disposers and stop timer (which can not auto-GC when component unmount)
   */
  componentWillUnmount() {
    this.stopTimer();

    for (const disposer of this.activeIDisposers) disposer();
  }

  /**
   * on bid order form price change
   */
  onBidPriceChanged = (price: BigNumber) => {
    console.debug(`onBidPriceChanged ${price}`);
    const { input } = this.bidState;
    const { orderVolumePrecision = 2 } = this.props;
    const { quoteTokenBalance = zero } = this;

    this.bidState.input.price = price;
    this.validateBidPrice(price);

    if (!input.amount || input.amount.isNaN()) return;

    if (price.isNaN()) {
      this.bidState.input.total = zero;
      this.bidState.input.percent = 0;
      return;
    }

    this.bidState.input.total = toPrecision(
      new BigNumber(input.amount).multipliedBy(price),
      orderVolumePrecision
    );
    const percent = this.bidState.input.total
      .div(quoteTokenBalance)
      .multipliedBy(100);
    this.bidState.input.percent = percent.isNaN()
      ? 0
      : Math.min(percent.toNumber(), 100);
  };

  /**
   * validate bid price
   *
   * @returns  the price is valid or not
   */
  validateBidPrice = (price: BigNumber) => {
    const { maxBidPriceLimit = 1.5, latestPrice = 0 } = this.props;
    const bnLastPrice = new BigNumber(latestPrice);

    if (price.lte(0)) {
      this.bidState.validation.priceValid = false;
      this.bidState.validation.priceInvalidMsg = `Buy price cannot be 0`;
      console.debug('Buy price cannot be 0');
      return false;
    }

    if (
      !bnLastPrice.eq(0) &&
      maxBidPriceLimit &&
      bnLastPrice.multipliedBy(maxBidPriceLimit).lt(price)
    ) {
      this.bidState.validation.priceValid = false;

      const percent = new BigNumber(maxBidPriceLimit)
        .multipliedBy(100)
        .toFixed(2);
      this.bidState.validation.priceInvalidMsg = `Buy price cannot be more than ${percent}% of latest price`;

      console.debug(
        `Buy price cannot be more than ${percent} % of latest price( ${bnLastPrice})`
      );
      return false;
    }

    this.bidState.validation.priceValid = true;
    this.bidState.validation.priceInvalidMsg = '';
    return true;
  };

  /**
   * on ask order form price change
   */
  onAskPriceChanged = (price: BigNumber) => {
    console.debug('onAskPriceChanged', price);
    const { input } = this.askState;
    const { orderVolumePrecision = 2 } = this.props;

    this.askState.input.price = price;
    this.validateAskPrice(price);

    if (!input.amount || input.amount.isNaN()) return;

    if (price.isNaN()) {
      this.askState.input.total = zero;
      return;
    }

    this.askState.input.total = toPrecision(
      new BigNumber(input.amount).multipliedBy(price),
      orderVolumePrecision
    );
  };

  /**
   * validate ask price
   *
   * @returns  the price is valid or not
   */
  validateAskPrice = (price: BigNumber) => {
    const { maxAskPriceLimit = 0.5, latestPrice = 0 } = this.props;
    const bnLastPrice = new BigNumber(latestPrice);

    if (price.lte(0)) {
      this.askState.validation.priceValid = false;
      this.askState.validation.priceInvalidMsg = `Ask price cannot be 0`;
      console.debug('Ask price cannot be 0');
      return false;
    }

    if (
      !bnLastPrice.eq(0) &&
      maxAskPriceLimit &&
      bnLastPrice.multipliedBy(maxAskPriceLimit).gt(price)
    ) {
      this.askState.validation.priceValid = false;

      const percent = new BigNumber(maxAskPriceLimit)
        .multipliedBy(100)
        .toFixed(2);
      this.askState.validation.priceInvalidMsg = `Ask price cannot be less than ${percent}% of latest price`;

      console.debug(
        `Ask price cannot be less than ${percent} % of latest price( ${bnLastPrice})`
      );
      return false;
    }

    this.askState.validation.priceValid = true;
    this.askState.validation.priceInvalidMsg = '';
    return true;
  };

  /**
   * The price precision cannot exceed the setting
   */
  checkPricePrecision = (price: BigNumber) => {
    const { orderPricePrecision = 2 } = this.props;
    const precision = price.dp();

    if (precision <= orderPricePrecision) return true;

    console.debug(`price precision exceed ${orderPricePrecision}`);
    return false;
  };

  /**
   * The amount precision cannot exceed the setting
   */
  checkAmountPrecision = (amount: BigNumber) => {
    const { orderVolumePrecision = 2 } = this.props;
    const precision = amount.dp();

    if (precision <= orderVolumePrecision) return true;

    console.debug(`amount precision exceed ${orderVolumePrecision}`);
    return false;
  };

  /**
   * on bid order form amount change
   */
  onBidAmountChanged = (amount: BigNumber) => {
    console.debug('onBidAmountChanged', amount);
    const { input } = this.bidState;
    const { orderVolumePrecision = 2 } = this.props;

    console.debug(`onBidAmountChanged ${amount.toString()}`);
    this.bidState.input.amount = amount;
    this.validateBidAmount(amount);

    if (!input.price || input.price.isNaN()) return;

    if (amount.isNaN()) {
      this.bidState.input.total = zero;
      this.bidState.input.percent = 0;
      return;
    }

    this.bidState.input.total = toPrecision(
      new BigNumber(input.price).multipliedBy(amount),
      orderVolumePrecision
    );
    const percent = this.bidState.input.total
      .div(this.quoteTokenBalance)
      .multipliedBy(100)
      .toNumber();
    this.bidState.input.percent = Math.min(percent, 100);
  };

  /**
   * validate bid amount
   */
  validateBidAmount = (amount: BigNumber) => {
    const { quoteTokenBalance = 0 } = this;
    const { orderVolumePrecision = 0 } = this.props;
    const bnPrice = new BigNumber(this.bidState.input.price);
    const quoteTokenBalanceBN = new BigNumber(quoteTokenBalance);

    if (amount.eq(0)) {
      this.bidState.validation.amountValid = false;
      this.bidState.validation.amountInvalidMsg = `Buy volume cannot be 0`;
      console.debug('Buy volume cannot be 0');
      return false;
    }

    if (bnPrice.gt(0) && quoteTokenBalance > 0) {
      const maxBidAmount = quoteTokenBalanceBN.div(bnPrice);

      if (amount.gt(maxBidAmount)) {
        const amountFmt = toPrecision(maxBidAmount, orderVolumePrecision);
        this.bidState.validation.amountValid = false;
        this.bidState.validation.amountInvalidMsg = `Bid amount cannot be more than ${amountFmt}`;
        console.debug(`Bid amount cannot be more than ${amountFmt}`);
        return false;
      }
    }
    this.bidState.validation.amountValid = true;
    this.bidState.validation.amountInvalidMsg = '';
    return true;
  };

  /**
   * on ask order form amount change
   */
  onAskAmountChanged = (amount: BigNumber) => {
    console.debug(`onAskAmountChanged ${amount}`);
    const { input } = this.askState;
    const { orderVolumePrecision = 2 } = this.props;

    this.askState.input.amount = amount;
    this.validateAskAmount(amount);

    if (amount.isNaN()) {
      this.askState.input.total = zero;
      this.askState.input.percent = 0;
      return;
    }
    if (this.baseTokenBalance.gt(0))
      this.askState.input.percent = new BigNumber(amount)
        .div(this.baseTokenBalance)
        .multipliedBy(100)
        .toNumber();
    else if (this.baseTokenBalance.eq(0) && amount.gt(0))
      this.askState.input.percent = 100;

    if (input.price.gt(0) && amount.gt(0))
      this.askState.input.total = toPrecision(
        new BigNumber(input.price).multipliedBy(amount),
        orderVolumePrecision
      );
  };

  /**
   * validate ask amount
   */
  validateAskAmount = (amount: BigNumber) => {
    const { baseTokenBalance = zero } = this;
    const { orderVolumePrecision = 0 } = this.props;

    if (amount.eq(0)) {
      this.askState.validation.amountValid = false;
      this.askState.validation.amountInvalidMsg = `Sell volume cannot be 0`;
      console.debug('Sell volume cannot be 0');
      return false;
    }

    if (baseTokenBalance.gt(0) && amount.gt(baseTokenBalance)) {
      const amountFmt = toPrecision(baseTokenBalance, orderVolumePrecision);
      this.askState.validation.amountValid = false;
      this.askState.validation.amountInvalidMsg = `Sell amount cannot be more than ${amountFmt}`;
      console.debug(`Sell amount cannot be more than ${amountFmt}`);
      return false;
    }

    this.askState.validation.amountValid = true;
    this.askState.validation.amountInvalidMsg = '';
    return true;
  };

  /**
   * on bid order form percent change
   */
  onBidSliderPercentChanged = (percent: number) => {
    const { input } = this.bidState;
    const { orderVolumePrecision = 2 } = this.props;

    this.bidState.input.percent = percent;

    if (this.quoteTokenBalance.eq(0) || !input.price) return;

    this.bidState.input.total = this.quoteTokenBalance
      .multipliedBy(percent)
      .div(100);

    this.bidState.input.amount = toPrecision(
      this.bidState.input.total.div(input.price),
      orderVolumePrecision
    );
    this.bidState.input.amountPrecision = orderVolumePrecision;
  };

  /**
   * on ask order form percent change
   */
  onAskSliderPercentChanged = (percent: number) => {
    const { input } = this.askState;
    const { orderVolumePrecision = 2 } = this.props;

    this.askState.input.percent = percent;
    this.askState.input.amount = this.baseTokenBalance
      .multipliedBy(percent)
      .div(100);

    if (this.baseTokenBalance.eq(0) || !input.price) return;

    this.askState.input.total = toPrecision(
      new BigNumber(this.askState.input.amount).multipliedBy(input.price),
      orderVolumePrecision
    );

    this.askState.input.amountPrecision = orderVolumePrecision;
  };

  /**
   * submit bid order
   */
  onBidOrderSubmitted = async () => {
    const { input } = this.bidState;
    const {
      marketId,
      baseToken,
      quoteToken,
      priceDecimals,
      onBidOrderSubmitted
    } = this.props;

    if (!this.validateBidPrice(input.price)) return;
    if (!this.validateBidAmount(input.amount)) return;

    const transferFee = DFT.calcTransferFee(input.amount, quoteToken);

    this.bidState.submitting = true;
    // approve first
    if (this.bidState.approvedAmount < input.amount.plus(transferFee))
      try {
        await this.quoteTokenActor.approveToken(
          marketId,
          toBigNumber(DEFAULT_APPROVE_AMOUNT),
          baseToken.decimals
        );
        console.debug('Approve quote token success');
      } catch (e) {
        console.warn('Approve quote token fail', e);
        if (e instanceof CanisterError) {
          toastStore.error(`code ${e.code}, ${e.message}`, 'Swap error');
        } else {
          toastStore.error(`Unknown error: ${e.message}`, 'Swap error');
        }
        return;
      } finally {
        this.bidState.submitting = false;
      }
    const priceBN = toBigInt(parseToOrigin(input.price, priceDecimals));
    const amountBN = toBigInt(parseToOrigin(input.amount, baseToken.decimals));

    if (!onBidOrderSubmitted) {
      console.debug('Bid order submit called');
      return;
    }

    try {
      const orderId = await onBidOrderSubmitted(priceBN, amountBN);

      toastStore.success(
        `price ${input.price}, volume ${input.amount}`,
        'Submit bid order success'
      );
      console.info('Bid order success', orderId);
    } catch (e) {
      toastStore.error(`${e?.message || ''}`, 'Submit ask order fail');
    } finally {
      this.bidState.submitting = false;
    }
  };

  /**
   * submit ask order
   * @returns
   */
  onAskOrderSubmitted = async () => {
    const { input } = this.askState;

    if (
      !this.validateAskPrice(input.price) ||
      !this.validateAskAmount(input.amount)
    )
      return;

    const { marketId, baseToken, priceDecimals, onAskOrderSubmitted } =
      this.props;
    const transferFee = DFT.calcTransferFee(input.amount, baseToken);

    this.askState.submitting = true;
    // approve first
    if (this.askState.approvedAmount < input.amount.plus(transferFee))
      try {
        await this.baseTokenActor.approveToken(
          marketId,
          toBigNumber(DEFAULT_APPROVE_AMOUNT),
          baseToken.decimals
        );
        console.debug('Approve base token success');
      } catch (e) {
        console.warn('Approve base token fail', e);
        if (e instanceof CanisterError) {
          toastStore.error(`code ${e.code}, ${e.message}`, 'Swap error');
        } else {
          toastStore.error(`Unknown error: ${e.message}`, 'Swap error');
        }
        return;
      } finally {
        this.askState.submitting = false;
      }

    const priceBN = toBigInt(parseToOrigin(input.price, priceDecimals));
    const amountBN = toBigInt(parseToOrigin(input.amount, baseToken.decimals));

    if (!onAskOrderSubmitted) {
      console.debug('Ask order submit called');
      return;
    }
    console.debug(
      `Ask order submit called: price ${input.price} amount ${input.amount}`
    );
    try {
      const orderId = await onAskOrderSubmitted(priceBN, amountBN);

      toastStore.success(
        `price ${input.price}, volume ${input.amount}`,
        'Submit ask order success'
      );
      console.info(`Ask order success ${orderId}`);
    } catch (e) {
      toastStore.error(`${e?.message || ''}`, 'Submit ask order fail');
    } finally {
      this.askState.submitting = false;
    }
  };

  renderForm(type: SpotOrderType) {
    const isBuy = type === SpotOrderType.Bid;
    const quoteTokenSymbol = this.props?.quoteToken?.symbol || '';
    const baseTokenSymbol = this.props?.baseToken?.symbol || '';
    const {
      baseTokenPrecision = 0,
      quoteTokenPrecision = 0,
      orderPricePrecision = 0,
      orderVolumePrecision = 0
    } = this.props;
    const actionState = isBuy ? this.bidState : this.askState;
    const {
      amount = zero,
      amountPrecision = 0,
      price = zero,
      pricePrecision = 0
    } = actionState.input;
    const validation = actionState.validation;
    const availableAmount = isBuy
      ? formatNumber(this.quoteTokenBalance.toNumber(), quoteTokenPrecision)
      : formatNumber(this.baseTokenBalance.toNumber(), baseTokenPrecision);

    return (
      <Form
        noValidate
        className={classNames('col-6', style['form-wrap'])}
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
          this.onTradeFormSubmit(type);
        }}
      >
        <header
          className={classNames(
            'd-flex align-items-center text-white',
            style.header
          )}
        >
          Avbl
          <span className="flex-fill text-secondary">
            <span className="mx-3">|</span>Fee 0.0%
          </span>
          {availableAmount}
          <Badge className="ms-2" bg="secondary">
            {isBuy ? quoteTokenSymbol : baseTokenSymbol}
          </Badge>
        </header>
        <InputGroup className={style['input-group']}>
          <Form.Control
            className="border-0 bg-info text-secondary fs-12"
            type="number"
            name="price"
            required
            placeholder="Price"
            value={price.toFixed(pricePrecision)}
            onChange={e => {
              const { value } = e.target;
              actionState.input.pricePrecision = Math.min(
                getPrecision(value),
                orderPricePrecision
              );
              console.debug('pricePrecision', actionState.input.pricePrecision);
              this.onTradeFormPriceChanged(toBigNumber(value), type);
            }}
            isInvalid={!validation.priceValid}
          />
          <InputGroup.Text className="border-0 bg-info text-secondary fs-12">
            {quoteTokenSymbol}
          </InputGroup.Text>
          <Form.Control.Feedback type="invalid" tooltip>
            {validation.priceInvalidMsg}
          </Form.Control.Feedback>
        </InputGroup>
        <InputGroup className={classNames(style['input-group'])}>
          <Form.Control
            className="border-0 bg-info text-secondary fs-12"
            type="number"
            name="quantity"
            required
            placeholder="Qty"
            value={amount.toFixed(amountPrecision)}
            onChange={e => {
              const { value } = e.target;
              actionState.input.amountPrecision = Math.min(
                getPrecision(value),
                orderVolumePrecision
              );
              console.debug(
                'amountPrecision',
                actionState.input.amountPrecision
              );
              this.onTradeFormAmountChanged(toBigNumber(value), type);
            }}
            isInvalid={!validation.amountValid}
          />
          <InputGroup.Text className="border-0 bg-info text-secondary fs-12">
            {baseTokenSymbol}
          </InputGroup.Text>
          <Form.Control.Feedback type="invalid" tooltip>
            {validation.amountInvalidMsg}
          </Form.Control.Feedback>
        </InputGroup>
        <div className={style['RangeInput-wrap']}>
          <RangeInput
            className={style.RangeInput}
            colors={
              isBuy
                ? ['rgba(0, 105, 92, .1)', 'rgba(0, 105, 92, 0.8)']
                : ['rgba(198, 40, 40, 0.1)', 'rgba(198, 40, 40, 0.8)']
            }
            value={actionState.input.percent}
            onChange={v => {
              this.onTradeFormSliderPercentChanged(v, type);
            }}
          />
        </div>

        <footer
          className={classNames(
            'd-flex align-items-center text-white',
            style.footer
          )}
        >
          <span className="flex-fill">Total</span>
          {formatNumber(
            actionState.input.total.toNumber(),
            quoteTokenPrecision
          )}
          <Badge className="ms-2" bg="secondary">
            {quoteTokenSymbol}
          </Badge>
        </footer>
        <ChooseWallet>
          <Button
            className={classNames(
              'w-100 text-capitalize fs-14',
              style.btn,
              isBuy ? style['btn-buy'] : style['btn-sell'],
              {
                [style['connect-btn']]: !session.isConnected
              }
            )}
            type="submit"
            disabled={actionState.submitting}
          >
            {!session.isConnected ? 'Connect Wallet' : isBuy ? 'Buy' : 'Sell'}
            {actionState.submitting && (
              <Spinner
                animation="border"
                size="sm"
                variant="primary"
                className={style.spinner}
              />
            )}
          </Button>
        </ChooseWallet>
      </Form>
    );
  }

  renderTowards() {
    return (
      <ButtonGroup className={style.towards}>
        <ToggleButton
          className="border-0 rounded-3 text-secondary fs-12"
          variant="transparent"
          size="sm"
          type="radio"
          name="toward"
          value="0"
        >
          Withdraw
        </ToggleButton>
        <ToggleButton
          className="border-0 rounded-3 bg-black text-secondary fs-12"
          variant="transparent"
          size="sm"
          type="radio"
          name="toward"
          value="1"
        >
          Deposit
        </ToggleButton>
      </ButtonGroup>
    );
  }

  render() {
    const { className } = this.props;

    return (
      <div
        className={classNames(
          'position-relative',
          'd-flex',
          'flex-column',
          className,
          style['trading-form-wrap']
        )}
      >
        <Tabs className={style.tabs}>
          <Tab eventKey="limit" title="Limit">
            <Row className="m-0">
              {this.renderForm(SpotOrderType.Bid)}
              {this.renderForm(SpotOrderType.Ask)}
            </Row>
          </Tab>
          <Tab eventKey="market" title="Market">
            <div className="h-100 d-flex justify-content-center align-items-center">
              Coming soon...
            </div>
          </Tab>
        </Tabs>

        {/* TODO: hide it now. {this.renderTowards()} */}
      </div>
    );
  }
}
